import { Logger, LoggerConnectionStatus } from '@oneappexchange/lwc-wired';
import { IS_DEVELOPMENT, LOGGER_PREFIX, LOGGER_SOURCE, LOGGER_WSS_ENDPOINT, VERSION } from '~/modules/config';
import { setCrossEnvLogger } from '~/modules/utilities/cross_env_utils';
import type { ExceptionPayload } from '~/modules/utilities/error_utilities';

// extend error fn to allow sending any number of args as a JSON oject
class AppxSiteLogger extends Logger {
    log = this.info;
    // TODO: Document why we're overriding this
    // @ts-expect-error * Automated Type Error Fix
    override error(...args) {
        if (args.length > 1) {
            if (typeof args[0] === 'string') {
                super.error(JSON.stringify({ message: args[0], data: args.length === 2 ? args[1] : args.slice(1) }));
            } else {
                super.error(JSON.stringify({ data: args }));
            }
        } else {
            super.error(args[0]);
        }
    }
    // Use console.debug to print `Awaiting logger socket connection, message enqueued ${finalMessage}`
    // @ts-expect-error * Automated Type Error Fix
    override send(level, message, type = 'log') {
        let stringifiedMessage;
        if (typeof message === 'string') {
            stringifiedMessage = message;
        } else {
            stringifiedMessage = JSON.stringify(message, null, 2);
        }
        const finalMessage = this.prefix ? this.prefix + stringifiedMessage : stringifiedMessage;
        if (level in console) {
            // @ts-expect-error * Automated Type Error Fix
            console[level](finalMessage);
        }
        if (this.status === LoggerConnectionStatus.Closed) {
            return;
        }
        type SplunkLogEnvelope = {
            type: string;
            level: string;
            time: string;
            source: string;
            version: string;
            message: string;
        } & Partial<ExceptionPayload>;

        const envelope: SplunkLogEnvelope = {
            type,
            level,
            time: new Date().toISOString(),
            source: this.source,
            version: VERSION,
            message: finalMessage,
        };
        // Spread structured JSON fields to top level for Splunk field extraction.
        // Arrays are excluded to avoid spreading numeric indices as top-level fields.
        try {
            const parsed: Partial<ExceptionPayload> = JSON.parse(stringifiedMessage);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                const {
                    type: _t,
                    level: _l,
                    time: _ti,
                    source: _s,
                    name: _n,
                    message: _m,
                    version: _v,
                    ...safe
                } = parsed;
                Object.assign(envelope, safe);
                if (parsed.name) {
                    envelope.type = parsed.name;
                }
                if (parsed.message) {
                    envelope.message = parsed.message;
                }
            }
        } catch {
            // Not JSON — leave message as-is
        }
        const logMessage = JSON.stringify(envelope);
        if (this.status === LoggerConnectionStatus.Connecting) {
            // @ts-expect-error * Automated Type Error Fix
            this.messageQueue.push(logMessage);
            console.debug(`Awaiting logger socket connection, message enqueued.`);
            return;
        }
        // @ts-expect-error * Automated Type Error Fix
        this.logSocket.send(logMessage);
    }
}

export const logger = setCrossEnvLogger(
    new AppxSiteLogger({
        wssEndpoint: LOGGER_WSS_ENDPOINT || '',
        prefix: `[${LOGGER_PREFIX}]: `,
        source: LOGGER_SOURCE,
        developmentMode: IS_DEVELOPMENT,
    }),
);
